import React, { useState, useEffect } from 'react';
import { db } from '../db';
import { ShieldAlert, Trash2, ArrowLeft, Send, CheckCircle, Clock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast, confirmDialog } from '../utils/feedback';

const AdminDashboard = ({ currentProfile }) => {
  const navigate = useNavigate();
  const [feedbacks, setFeedbacks] = useState([]);
  const [filter, setFilter] = useState('new'); // 'new' or 'processed'
  const [replyMessages, setReplyMessages] = useState({});
  const [submittingReply, setSubmittingReply] = useState({});
  const isAdmin = currentProfile?.id === 'VX3fSmgFvr20XLYuzZv8';

  useEffect(() => {
    if (isAdmin) {
      loadFeedbacks();
    } else {
      navigate('/');
    }
  }, [isAdmin, navigate]);

  const loadFeedbacks = async () => {
    const fbs = await db.getFeedbacks();
    setFeedbacks(fbs);
  };

  const handleDeleteFeedback = async (id) => {
    confirmDialog("Supprimer ce retour ?", async () => {
      await db.deleteFeedback(id);
      setFeedbacks(prev => prev.filter(f => f.id !== id));
      toast.success("Retour supprimé.");
    });
  };

  const handleToggleProcessed = async (fb) => {
    const newStatus = !fb.processed;
    await db.updateFeedback(fb.id, { processed: newStatus });
    setFeedbacks(prev => prev.map(f => f.id === fb.id ? { ...f, processed: newStatus } : f));
    toast.success(newStatus ? "Marqué comme traité" : "Déplacé vers les nouveaux");
  };

  const handleReply = async (fb) => {
    const msg = replyMessages[fb.id]?.trim();
    if (!msg) return;

    setSubmittingReply(prev => ({ ...prev, [fb.id]: true }));
    try {
      await db.updateFeedback(fb.id, { processed: true, replyMessage: msg });
      
      if (fb.profileId && fb.profileId !== 'anonymous') {
        await db.addNotification({
          targetProfileId: fb.profileId,
          message: `L'administrateur a répondu à ton retour. Clique pour lire la réponse.`,
          fullMessage: msg,
          link: '', // We intercept this in App.jsx
          type: 'feedback_reply'
        });
      }
      
      setFeedbacks(prev => prev.map(f => f.id === fb.id ? { ...f, processed: true, replyMessage: msg } : f));
      setReplyMessages(prev => ({ ...prev, [fb.id]: '' }));
      toast.success("Réponse envoyée et retour traité !");
    } catch (e) {
      console.error(e);
      toast.error("Erreur lors de l'envoi de la réponse.");
    } finally {
      setSubmittingReply(prev => ({ ...prev, [fb.id]: false }));
    }
  };

  if (!isAdmin) return null;

  const filteredFeedbacks = feedbacks.filter(fb => filter === 'processed' ? fb.processed : !fb.processed);

  return (
    <div style={{ padding: '1rem', maxWidth: '1000px', margin: '0 auto', animation: 'fadeIn 0.3s ease-out', paddingBottom: '3rem' }}>
      <button className="btn" onClick={() => navigate(-1)} style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem', backgroundColor: 'var(--bg-card)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }}>
        <ArrowLeft size={20} /> Retour
      </button>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginTop: '1rem' }}>
        <h2 style={{ color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '0.5rem', borderBottom: '1px solid var(--border-color)', paddingBottom: '0.5rem' }}>
          <ShieldAlert size={24} color="var(--danger-color)" /> Panel Administrateur
        </h2>
        
        {/* TABS */}
        <div style={{ display: 'flex', gap: '1rem', borderBottom: '1px solid var(--border-color)', marginBottom: '1rem' }}>
          <button 
            className={`btn ${filter === 'new' ? '' : 'btn-outline'}`}
            style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', borderBottomLeftRadius: 0, borderBottomRightRadius: 0, backgroundColor: filter === 'new' ? 'var(--primary-color)' : 'transparent', color: filter === 'new' ? 'white' : 'var(--text-primary)', border: filter === 'new' ? 'none' : 'none', borderBottom: filter === 'new' ? '3px solid var(--secondary-color)' : '3px solid transparent' }}
            onClick={() => setFilter('new')}
          >
            <Clock size={18} /> Nouveaux retours
          </button>
          <button 
            className={`btn ${filter === 'processed' ? '' : 'btn-outline'}`}
            style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', borderBottomLeftRadius: 0, borderBottomRightRadius: 0, backgroundColor: filter === 'processed' ? 'var(--primary-color)' : 'transparent', color: filter === 'processed' ? 'white' : 'var(--text-primary)', border: filter === 'processed' ? 'none' : 'none', borderBottom: filter === 'processed' ? '3px solid var(--secondary-color)' : '3px solid transparent' }}
            onClick={() => setFilter('processed')}
          >
            <CheckCircle size={18} /> Traités
          </button>
        </div>

        {filteredFeedbacks.length === 0 ? (
          <div className="card text-center text-secondary">Aucun retour dans cette catégorie.</div>
        ) : (
          filteredFeedbacks.map(fb => (
            <div key={fb.id} className="card" style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', margin: 0 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <span style={{ fontWeight: 'bold', color: 'var(--text-primary)', fontSize: '1.1rem' }}>{fb.profileName}</span>
                    <span style={{ fontSize: '0.8rem', padding: '0.2rem 0.5rem', borderRadius: 'var(--border-radius-sm)', backgroundColor: fb.type === 'bug' ? 'var(--danger-color)' : 'var(--primary-color)', color: 'white' }}>
                      {fb.type.toUpperCase()}
                    </span>
                  </div>
                  <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                    Compte : {fb.userEmail || 'Inconnu'}
                  </span>
                </div>
                <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                  {new Date(fb.timestamp).toLocaleDateString('fr-FR')} {new Date(fb.timestamp).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                </span>
              </div>
              
              <p style={{ color: 'var(--text-primary)', whiteSpace: 'pre-wrap', wordBreak: 'break-word', overflowWrap: 'break-word', backgroundColor: 'var(--bg-color)', padding: '1rem', borderRadius: 'var(--border-radius-sm)', border: '1px solid var(--border-color)', marginTop: '0.5rem' }}>
                {fb.message}
              </p>

              {fb.replyMessage && (
                <div style={{ marginTop: '0.5rem', padding: '0.8rem', backgroundColor: 'rgba(168, 159, 207, 0.1)', borderLeft: '4px solid var(--secondary-color)', borderRadius: 'var(--border-radius-sm)' }}>
                  <strong style={{ display: 'block', fontSize: '0.85rem', color: 'var(--secondary-color)', marginBottom: '0.3rem' }}>Ta réponse :</strong>
                  <span style={{ color: 'var(--text-primary)', fontSize: '0.9rem', whiteSpace: 'pre-wrap' }}>{fb.replyMessage}</span>
                </div>
              )}

              {filter === 'new' && (
                <div style={{ marginTop: '0.5rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  <textarea 
                    className="form-control"
                    placeholder="Écrire une réponse à l'utilisateur..."
                    rows="3"
                    value={replyMessages[fb.id] || ''}
                    onChange={e => setReplyMessages(prev => ({ ...prev, [fb.id]: e.target.value }))}
                    style={{ fontSize: '0.9rem' }}
                  ></textarea>
                  <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', alignItems: 'center' }}>
                    <button className="btn-icon" style={{ color: 'var(--danger-color)', marginRight: 'auto' }} onClick={() => handleDeleteFeedback(fb.id)} title="Supprimer">
                      <Trash2 size={18} />
                    </button>
                    <button className="btn btn-outline" onClick={() => handleToggleProcessed(fb)}>
                      <CheckCircle size={16} style={{ marginRight: '0.4rem', verticalAlign: 'middle' }} /> Marquer comme traité
                    </button>
                    <button 
                      className="btn btn-primary" 
                      onClick={() => handleReply(fb)}
                      disabled={!replyMessages[fb.id]?.trim() || submittingReply[fb.id]}
                    >
                      <Send size={16} style={{ marginRight: '0.4rem', verticalAlign: 'middle' }} /> 
                      {submittingReply[fb.id] ? 'Envoi...' : 'Répondre'}
                    </button>
                  </div>
                </div>
              )}

              {filter === 'processed' && (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '0.5rem' }}>
                  <button className="btn btn-outline" style={{ padding: '0.4rem 0.8rem', fontSize: '0.8rem' }} onClick={() => handleToggleProcessed(fb)}>
                    Remettre dans les nouveaux
                  </button>
                  <button className="btn-icon" style={{ color: 'var(--danger-color)' }} onClick={() => handleDeleteFeedback(fb.id)} title="Supprimer">
                    <Trash2 size={18} />
                  </button>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
